/**
 * <featured-collections> — Phase 1 done (static list, hover animation); Phase 2/3 (split/overlay
 * layout preview) TODO. IIFE-wrapped since the asset loads once per section (avoids re-declare).
 */
(() => {
  class FeaturedCollections extends HTMLElement {
    connectedCallback() {
      this.layout = this.dataset.layout;

      if (this.layout === 'split') {
        this.initSplitCursorPreview();
      } else if (this.layout === 'overlay') {
        this.initOverlay();
      }
    }

    disconnectedCallback() {
      if (this._stopLoop) this._stopLoop();
      if (this._stopAutoplay) this._stopAutoplay();
    }

    // Split layout: a cursor-following preview card whose image track scrolls to the hovered
    // collection. Desktop pointer only — the preview is hidden on touch / small screens in CSS.
    initSplitCursorPreview() {
      const panel = this.querySelector('.featured-collections__panel');
      const follower = this.querySelector('.featured-collections__preview--cursor');
      const track = this.querySelector('.featured-collections__preview-track');
      const list = this.querySelector('.featured-collections__list');
      if (!panel || !follower || !track || !list) return;

      const links = Array.from(
        list.querySelectorAll('.featured-collections__link[data-index]')
      );
      if (links.length === 0) return;

      // Disabled below 990px and on devices without a real pointer (touch).
      const desktopPointer = window.matchMedia(
        '(min-width: 990px) and (hover: hover) and (pointer: fine)'
      );
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Rest the card to the bottom-right of the cursor; trail it with eased inertia in a rAF loop
      // that runs only while the cursor is over the list.
      const OFFSET_X = 24;
      const OFFSET_Y = 24;
      const EASE = 0.16;

      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;
      let rafId = null;
      let active = false;

      const setIndex = (index) => {
        track.style.setProperty('--fc-active', index);
      };

      const updateTarget = (clientX, clientY) => {
        const rect = panel.getBoundingClientRect();
        const w = follower.offsetWidth;
        const h = follower.offsetHeight;
        const x = clientX - rect.left + OFFSET_X;
        const y = clientY - rect.top + OFFSET_Y;
        // Keep the card inside the panel so it never spills past the rounded edges.
        targetX = Math.max(0, Math.min(x, rect.width - w));
        targetY = Math.max(0, Math.min(y, rect.height - h));
      };

      const applyTransform = () => {
        follower.style.transform = `translate(${currentX}px, ${currentY}px)`;
      };

      const tick = () => {
        currentX += (targetX - currentX) * EASE;
        currentY += (targetY - currentY) * EASE;
        applyTransform();
        rafId = requestAnimationFrame(tick);
      };

      const stopLoop = () => {
        if (rafId != null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      };
      this._stopLoop = stopLoop;

      const show = (event) => {
        if (!desktopPointer.matches) return;
        updateTarget(event.clientX, event.clientY);
        // Snap to the cursor on appearance; the trailing weight only applies to movement.
        currentX = targetX;
        currentY = targetY;
        applyTransform();
        this.classList.add('is-preview-visible');
        active = true;
        if (!reduced) {
          stopLoop();
          rafId = requestAnimationFrame(tick);
        }
      };

      const move = (event) => {
        if (!active) return;
        updateTarget(event.clientX, event.clientY);
        if (reduced) {
          currentX = targetX;
          currentY = targetY;
          applyTransform();
        }
      };

      const hide = () => {
        this.classList.remove('is-preview-visible');
        active = false;
        stopLoop();
      };

      list.addEventListener('mouseenter', show);
      list.addEventListener('mousemove', move);
      list.addEventListener('mouseleave', hide);

      links.forEach((link) => {
        link.addEventListener('mouseenter', () => {
          setIndex(parseInt(link.dataset.index, 10) || 0);
        });
      });
    }

    // Overlay layout: one always-active link drives the corner preview image. Hovering a link makes
    // it active (and pauses autoplay); autoplay cycles the active link on a timer otherwise. The whole
    // active-state + autoplay behaviour is tied to the corner preview (>= 990px, slider enabled) — when
    // the slider is off the track isn't rendered, so we bail out and leave plain links.
    initOverlay() {
      const list = this.querySelector('.featured-collections__list');
      const track = this.querySelector(
        '.featured-collections__preview--corner .featured-collections__preview-track'
      );
      const links = Array.from(
        this.querySelectorAll('.featured-collections__link[data-index]')
      );
      if (links.length === 0 || !track) return;

      const count = links.length;
      let activeIndex = 0;

      const setActive = (index) => {
        activeIndex = ((index % count) + count) % count;
        links.forEach((link, i) => link.classList.toggle('is-active', i === activeIndex));
        track.style.setProperty('--fc-active', activeIndex);
      };

      // Autoplay — only when enabled, at >= 990px (where the corner preview shows), with more than one
      // collection, and not paused.
      const enabledMq = window.matchMedia('(min-width: 990px)');
      const autoplayOn = this.dataset.autoplay === 'true';
      const duration = (parseFloat(this.dataset.autoplayDuration) || 5) * 1000;

      let timer = null;
      let paused = false;

      const stop = () => {
        if (timer != null) {
          clearInterval(timer);
          timer = null;
        }
      };
      const start = () => {
        stop();
        if (!autoplayOn || paused || count < 2 || !enabledMq.matches) return;
        timer = setInterval(() => setActive(activeIndex + 1), duration);
      };
      this._stopAutoplay = stop;

      links.forEach((link) => {
        link.addEventListener('mouseenter', () => {
          setActive(parseInt(link.dataset.index, 10) || 0);
          paused = true;
          stop();
        });
      });

      if (list) {
        list.addEventListener('mouseleave', () => {
          paused = false;
          start();
        });
      }

      // Pause while the tab is hidden so the active link doesn't race ahead in the background.
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop();
        else start();
      });

      // Stop/restart autoplay as the viewport crosses the 750px threshold.
      enabledMq.addEventListener('change', start);

      setActive(0);
      start();
    }
  }

  if (!customElements.get('featured-collections')) {
    customElements.define('featured-collections', FeaturedCollections);
  }
})();
